/**
 * Risk Management Validation Utilities
 * 
 * Centralized validation logic for risk management controls
 * to ensure consistent dependency checking across components.
 */

import { StopLossConfig, TargetProfitConfig, TrailingStopLossConfig, MTMType } from '../../types/strategy';
import { MTM_TYPE, TARGET_PROFIT_TYPE } from '../../constants/tradingConstants';

// Percentage-family TP types (premium + UL). The MAX_PREMIUM_PERCENT
// fat-finger cap fires on any of these — UL_PERCENTAGE was missed pre-Phase 6
// review-fix H5 and would accept arbitrarily large values (e.g. 9999) that
// the backend tolerates but never fires. Cross-checked against backend
// `is_ul_target_config` predicate.
const PERCENT_TP_TYPES: readonly string[] = [
  TARGET_PROFIT_TYPE.PERCENTAGE,
  TARGET_PROFIT_TYPE.UL_PERCENTAGE,
];

/**
 * Upper bound for a percentage-based risk threshold (target-profit %, and the
 * relaxed Combined-Premium % MTM cap). A fat-finger guard, not a hard rule — the
 * engine tolerates any positive value. Kept in one place (and mirrored by the
 * backend `_MAX_PREMIUM_PCT`) so the leg-level and strategy-level caps can't drift.
 */
export const MAX_PREMIUM_PERCENT = 1000;

/**
 * Validates if a Stop Loss configuration is complete and valid
 * @param stopLoss - Stop Loss configuration to validate
 * @returns true if Stop Loss is enabled with valid settings
 */
export const isValidStopLoss = (stopLoss: StopLossConfig | undefined): boolean => {
  if (!stopLoss) return false;
  if (!stopLoss.enabled) return false;
  if (stopLoss.type === 'RANGE') return !!stopLoss.rangeSide;
  return stopLoss.value > 0;
};

/**
 * Validates if a Target Profit configuration is complete and valid
 * @param targetProfit - Target Profit configuration to validate
 * @returns true if Target Profit is enabled with valid settings
 */
export const isValidTargetProfit = (targetProfit: TargetProfitConfig | undefined): boolean => {
  if (!targetProfit) return false;
  if (!targetProfit.enabled) return false;
  if (targetProfit.value <= 0) return false;
  // Fat-finger cap: percentage-family TP (premium + UL) is capped at
  // MAX_PREMIUM_PERCENT to catch typos like 9999 for 99.99. Points-family
  // types have no cap (a large points TP is inert, not dangerous).
  if (PERCENT_TP_TYPES.includes(targetProfit.type) && targetProfit.value > MAX_PREMIUM_PERCENT) {
    return false;
  }
  return true;
};

/**
 * Validates a strategy-level MTM target/stop config ({ type, value }).
 * A value of 0 means "disabled" and is valid. Returns an invalid-reason string
 * (for inline display), or null when valid.
 *
 * COMBINED_PREMIUM_PERCENT is a % move of the combined premium against you (stop
 * loss) or for you (target). Whether that move can exceed 100% depends on the leg
 * mix, so the cap is leg-aware via `allowAbove100` (callers derive it from
 * `legExposure`): the stop loss passes `hasSellLeg` (a loss can exceed 100% of the
 * entry premium only with short exposure — a long-only book's max loss is the
 * premium paid), the target passes `hasBuyLeg` (a profit can exceed 100% only with a
 * long leg — an all-SELL book's max profit is the premium collected).
 *
 * When allowed the cap opens to MAX_PREMIUM_PERCENT (1000); default false =
 * fail-closed (the old 0–100 cap). The engine tolerates any positive threshold, and
 * on a book that can't reach the value the stop/target simply never fires — so a
 * too-high value is inert rather than dangerous. That inertness is exactly why the
 * 1000 bound matters: once relaxed, it is the only guard against a fat-finger typo.
 *
 * @param config        - { type: MTMType; value: number } (targetProfit or mtmStopLoss)
 * @param label         - human label used in the message ("Strategy Target Profit" etc.)
 * @param allowAbove100 - permit a Combined-Premium % above 100 (up to MAX_PREMIUM_PERCENT)
 */
export const getStrategyMtmInvalidReason = (
  config: { type: MTMType; value: number } | undefined,
  label: string,
  allowAbove100 = false
): string | null => {
  if (!config) return null;
  const value = Number(config.value) || 0;
  if (value < 0) return `${label} cannot be negative`;
  if (config.type === MTM_TYPE.COMBINED_PREMIUM_PERCENT) {
    const max = allowAbove100 ? MAX_PREMIUM_PERCENT : 100;
    if (value > max) {
      return `${label} (% of premium) must be between 0 and ${max}`;
    }
  }
  return null;
};

/**
 * Gets the reason why Stop Loss is invalid (for user feedback)
 * @param stopLoss - Stop Loss configuration to check
 * @returns string describing the issue, or null if valid
 */
export const getStopLossInvalidReason = (stopLoss: StopLossConfig | undefined): string | null => {
  if (!stopLoss) {
    return 'Stop Loss configuration is missing';
  }
  if (!stopLoss.enabled) {
    return 'Stop Loss must be enabled';
  }
  if (stopLoss.value <= 0) {
    return 'Stop Loss value must be greater than 0';
  }
  return null;
};

/**
 * Validates if Trailing Stop Loss can be enabled based on Stop Loss
 * @param stopLoss - Stop Loss configuration
 * @param trailingStopLoss - Trailing Stop Loss configuration
 * @returns true if Trailing Stop Loss is valid
 */
export const canEnableTrailingStopLoss = (stopLoss: StopLossConfig | undefined, trailingStopLoss: TrailingStopLossConfig | undefined): boolean => {
  return isValidStopLoss(stopLoss);
};

/**
 * Validates if a Trailing Stop Loss configuration is complete and valid.
 * Mirrors the backend gate in strategy_manager.validate_and_enhance_legs.
 * @param trailingStopLoss - Trailing Stop Loss configuration to validate
 * @param stopLoss - Stop Loss configuration (TSL requires a valid SL)
 * @returns true if TSL is enabled with valid settings
 */
export const isValidTrailingStopLoss = (
  trailingStopLoss: TrailingStopLossConfig | undefined,
  stopLoss: StopLossConfig | undefined,
): boolean => {
  if (!trailingStopLoss) return false;
  if (!trailingStopLoss.enabled) return false;
  if (!isValidStopLoss(stopLoss)) return false;
  return (trailingStopLoss.instrumentMoveValue || 0) > 0
      && (trailingStopLoss.stopLossMoveValue || 0) > 0;
};

/**
 * Gets the reason why Trailing Stop Loss is invalid (for user feedback).
 * @param trailingStopLoss - Trailing Stop Loss configuration to check
 * @param stopLoss - Stop Loss configuration (TSL requires a valid SL)
 * @returns string describing the issue, or null if valid
 */
export const getTrailingStopLossInvalidReason = (
  trailingStopLoss: TrailingStopLossConfig | undefined,
  stopLoss: StopLossConfig | undefined,
): string | null => {
  if (!trailingStopLoss) return 'Trailing Stop Loss configuration is missing';
  if (!trailingStopLoss.enabled) return 'Trailing Stop Loss must be enabled';
  if (!isValidStopLoss(stopLoss)) return 'Trailing Stop Loss requires a valid Stop Loss';
  if ((trailingStopLoss.instrumentMoveValue || 0) <= 0) return 'Instrument move value must be greater than 0';
  if ((trailingStopLoss.stopLossMoveValue || 0) <= 0) return 'Stop Loss move value must be greater than 0';
  return null;
};

/**
 * Validates if Re Entry can be enabled based on Stop Loss
 * @param stopLoss - Stop Loss configuration
 * @returns true if Re Entry can be enabled
 */
export const canEnableReEntry = (stopLoss: StopLossConfig | undefined): boolean => {
  return isValidStopLoss(stopLoss);
};

/**
 * Gets the reason why Target Profit is invalid (for user feedback)
 * @param targetProfit - Target Profit configuration to check
 * @returns string describing the issue, or null if valid
 */
export const getTargetProfitInvalidReason = (targetProfit: TargetProfitConfig | undefined): string | null => {
  if (!targetProfit) {
    return 'Target Profit configuration is missing';
  }
  if (!targetProfit.enabled) {
    return 'Target Profit must be enabled';
  }
  if (targetProfit.value <= 0) {
    return 'Target Profit value must be greater than 0';
  }
  if (PERCENT_TP_TYPES.includes(targetProfit.type) && targetProfit.value > MAX_PREMIUM_PERCENT) {
    return 'Target Profit percentage is too high';
  }
  return null;
};

/**
 * Validates if Re Execute can be enabled based on Target Profit
 * @param targetProfit - Target Profit configuration
 * @returns true if Re Execute can be enabled
 */
export const canEnableReExecute = (targetProfit: TargetProfitConfig | undefined): boolean => {
  return isValidTargetProfit(targetProfit);
};